import { useState, useEffect, useRef } from 'react';
import { Diamond, Lock, FileText, Target, MessageSquare, Check, ArrowRight, Download, Loader2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Proof() {
  const [synthesis, setSynthesis] = useState(null);
  const [rolefit, setRolefit] = useState(null);
  const [proof, setProof] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rolefitOpen, setRolefitOpen] = useState(false);
  const [rolefitForm, setRolefitForm] = useState({ job_title: '', job_description: '' });
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!proof) return;
    
    setDownloading(true);
    try {
      // Create a printable HTML document
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Proof Document - ${rolefit?.job_title || 'Leadership Profile'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Georgia', serif; 
              line-height: 1.6; 
              color: #1a1a1a;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header { 
              text-align: center; 
              margin-bottom: 40px;
              padding-bottom: 20px;
              border-bottom: 2px solid #d4a853;
            }
            .logo { font-size: 24px; font-weight: bold; color: #d4a853; margin-bottom: 8px; }
            .title { font-size: 28px; color: #1a1a1a; margin-bottom: 4px; }
            .subtitle { font-size: 14px; color: #666; }
            .section { margin-bottom: 32px; }
            .section-title { 
              font-size: 18px; 
              font-weight: bold; 
              color: #d4a853;
              margin-bottom: 12px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .section-content { color: #333; }
            .strengths { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
            .strength-tag { 
              background: #f5f0e6; 
              color: #8b6914;
              padding: 6px 12px; 
              border-radius: 20px;
              font-size: 13px;
            }
            .story { 
              background: #f9f9f9; 
              padding: 20px; 
              margin-bottom: 16px;
              border-left: 3px solid #d4a853;
            }
            .story-title { font-weight: bold; margin-bottom: 12px; color: #1a1a1a; }
            .story-section { margin-bottom: 8px; }
            .story-label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; }
            .footer { 
              margin-top: 40px; 
              padding-top: 20px; 
              border-top: 1px solid #ddd;
              text-align: center;
              font-size: 12px;
              color: #999;
            }
            @media print {
              body { padding: 20px; }
              .story { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">STRATA RECORD</div>
            <div class="title">Proof Document</div>
            <div class="subtitle">${rolefit?.job_title ? `Target Role: ${rolefit.job_title}` : 'Leadership Readiness Profile'}</div>
          </div>

          <div class="section">
            <div class="section-title">Executive Summary</div>
            <div class="section-content">${proof.executive_summary || ''}</div>
          </div>

          ${proof.key_strengths?.length > 0 ? `
          <div class="section">
            <div class="section-title">Key Strengths</div>
            <div class="strengths">
              ${proof.key_strengths.map(s => `<span class="strength-tag">${s}</span>`).join('')}
            </div>
          </div>
          ` : ''}

          ${proof.narratives?.length > 0 ? `
          <div class="section">
            <div class="section-title">Leadership Stories (STAR Format)</div>
            ${proof.narratives.map(story => `
              <div class="story">
                <div class="story-title">${story.title || 'Leadership Story'}</div>
                ${story.situation ? `<div class="story-section"><span class="story-label">Situation:</span> ${story.situation}</div>` : ''}
                ${story.task ? `<div class="story-section"><span class="story-label">Task:</span> ${story.task}</div>` : ''}
                ${story.action ? `<div class="story-section"><span class="story-label">Action:</span> ${story.action}</div>` : ''}
                ${story.result ? `<div class="story-section"><span class="story-label">Result:</span> ${story.result}</div>` : ''}
              </div>
            `).join('')}
          </div>
          ` : ''}

          ${proof.growth_edges?.length > 0 || proof.development_areas?.length > 0 ? `
          <div class="section">
            <div class="section-title">Growth Edge Strategy</div>
            <p style="margin-bottom: 12px; color: #666; font-style: italic;">Where this role will accelerate your development</p>
            <ul style="padding-left: 20px;">
              ${(proof.growth_edges || proof.development_areas || []).map(area => `<li style="margin-bottom: 8px;">${area}</li>`).join('')}
            </ul>
          </div>
          ` : ''}

          ${proof.interview_strategy ? `
          <div class="section">
            <div class="section-title">Interview Strategy</div>
            <div class="section-content">${proof.interview_strategy}</div>
          </div>
          ` : ''}

          <div class="footer">
            Generated by Strata Record • ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </body>
        </html>
      `;

      // Create a blob and open print dialog
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Wait for content to load then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
        
        toast.success('PDF ready - use Save as PDF in print dialog');
      } else {
        toast.error('Please allow popups to download PDF');
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [synthesisRes, rolefitRes, proofRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/synthesis`, { credentials: 'include' }),
        fetch(`${API_URL}/api/rolefit`, { credentials: 'include' }),
        fetch(`${API_URL}/api/proof`, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' })
      ]);

      if (synthesisRes.ok) setSynthesis(await synthesisRes.json());
      if (rolefitRes.ok) setRolefit(await rolefitRes.json());
      if (proofRes.ok) setProof(await proofRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (feature) => {
    setPurchasing(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feature,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout');

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCreateRolefit = async () => {
    if (!rolefitForm.job_title && !rolefitForm.job_description) {
      toast.error('Please provide a job title or description');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/rolefit/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(rolefitForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create role fit');
      }

      const data = await response.json();
      setRolefit(data);
      setRolefitOpen(false);
      toast.success('Role Fit analysis complete!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateProof = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/proof/create`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create proof');
      }

      const data = await response.json();
      setProof(data);
      toast.success('Proof document created!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const hasRoleFit = stats?.purchased_features?.includes('role_fit') || stats?.purchased_features?.includes('proof_bundle');
  const hasProof = stats?.purchased_features?.includes('proof') || stats?.purchased_features?.includes('proof_bundle');
  const hasSynthesis = !!synthesis;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="proof-page">
        {/* Header */}
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Proof</h1>
          <p className="text-slate-400">
            Create compelling evidence of your leadership readiness for opportunities.
          </p>
        </div>

        {/* Prerequisite check */}
        {!hasSynthesis && (
          <Card className="strata-card border-amber-600/30">
            <CardContent className="py-8 text-center">
              <Lock className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h3 className="font-display text-xl text-white mb-2">Strata Record Synthesis Required</h3>
              <p className="text-slate-400 mb-4">
                You need a Strata Record Synthesis before creating Role Fit analyses or Proof documents.
              </p>
              <Button onClick={() => window.location.href = '/insights'} className="strata-button-primary">
                Go to Insights
              </Button>
            </CardContent>
          </Card>
        )}

        {hasSynthesis && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Role Fit Card */}
            <Card className={`strata-card ${rolefit ? 'border-green-500/30' : ''}`}>
              <CardHeader>
                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-state-execute" />
                  Role Fit Analysis
                  {rolefit && <Check className="w-5 h-5 text-green-500 ml-auto" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rolefit ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Fit Score</span>
                      <span className="font-mono text-3xl text-white">{rolefit.fit_score}%</span>
                    </div>
                    
                    {rolefit.job_title && (
                      <div>
                        <p className="text-slate-500 text-sm">Target Role</p>
                        <p className="text-white">{rolefit.job_title}</p>
                      </div>
                    )}

                    {/* Fit Heatmap */}
                    {rolefit.fit_heatmap && Object.keys(rolefit.fit_heatmap).length > 0 && (
                      <div className="space-y-2 pt-4 border-t border-slate-mid/50">
                        <p className="text-slate-400 text-sm mb-3">State Fit Analysis</p>
                        {Object.entries(rolefit.fit_heatmap).map(([state, scores]) => (
                          <div key={state} className="flex items-center gap-3">
                            <span className="w-20 text-slate-400 text-sm capitalize">{state}</span>
                            <div className="flex-1 h-2 bg-slate-mid rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-amber-500 rounded-full transition-all"
                                style={{ width: `${scores.match || 0}%` }}
                              />
                            </div>
                            <span className="font-mono text-sm text-white w-12 text-right">
                              {scores.match || 0}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Interview Prep */}
                    {rolefit.interview_prep?.length > 0 && (
                      <div className="pt-4 border-t border-slate-mid/50">
                        <p className="text-slate-400 text-sm mb-2">Interview Questions to Prepare</p>
                        <ul className="space-y-2">
                          {rolefit.interview_prep.slice(0, 3).map((q, idx) => (
                            <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-amber-500">•</span>
                              {q}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Dialog open={rolefitOpen} onOpenChange={setRolefitOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full strata-button-secondary mt-4">
                          Create New Analysis
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate border-slate-mid">
                        <DialogHeader>
                          <DialogTitle className="font-display text-xl text-white">New Role Fit Analysis</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label className="text-slate-300">Job Title</Label>
                            <Input
                              value={rolefitForm.job_title}
                              onChange={(e) => setRolefitForm(prev => ({ ...prev, job_title: e.target.value }))}
                              placeholder="e.g., Chief Product Officer"
                              className="strata-input mt-2"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Job Description (Optional)</Label>
                            <Textarea
                              value={rolefitForm.job_description}
                              onChange={(e) => setRolefitForm(prev => ({ ...prev, job_description: e.target.value }))}
                              placeholder="Paste the job description..."
                              className="strata-input mt-2 h-32"
                            />
                          </div>
                          <Button 
                            onClick={handleCreateRolefit}
                            disabled={creating}
                            className="w-full strata-button-primary"
                          >
                            {creating ? 'Analyzing...' : 'Create Analysis'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                ) : hasRoleFit ? (
                  <div className="space-y-4">
                    <p className="text-slate-400">
                      Analyze how your leadership profile matches specific roles and opportunities.
                    </p>
                    <Dialog open={rolefitOpen} onOpenChange={setRolefitOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full strata-button-primary" data-testid="create-rolefit-btn">
                          Create Role Fit Analysis
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate border-slate-mid">
                        <DialogHeader>
                          <DialogTitle className="font-display text-xl text-white">Role Fit Analysis</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label className="text-slate-300">Job Title</Label>
                            <Input
                              value={rolefitForm.job_title}
                              onChange={(e) => setRolefitForm(prev => ({ ...prev, job_title: e.target.value }))}
                              placeholder="e.g., Chief Product Officer"
                              className="strata-input mt-2"
                              data-testid="rolefit-title"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Job Description (Optional)</Label>
                            <Textarea
                              value={rolefitForm.job_description}
                              onChange={(e) => setRolefitForm(prev => ({ ...prev, job_description: e.target.value }))}
                              placeholder="Paste the job description..."
                              className="strata-input mt-2 h-32"
                              data-testid="rolefit-description"
                            />
                          </div>
                          <Button 
                            onClick={handleCreateRolefit}
                            disabled={creating}
                            className="w-full strata-button-primary"
                            data-testid="submit-rolefit-btn"
                          >
                            {creating ? 'Analyzing...' : 'Create Analysis'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-slate-400">
                      Analyze how your leadership profile matches specific roles and opportunities.
                    </p>
                    <ul className="space-y-2">
                      {['Fit score calculation', 'Strengths alignment analysis', 'Interview question prep'].map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-4 h-4 text-amber-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handlePurchase('role_fit')}
                      disabled={purchasing}
                      className="w-full strata-button-primary"
                      data-testid="purchase-rolefit-btn"
                    >
                      {purchasing ? 'Processing...' : 'Unlock for $39'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Proof Document Card */}
            <Card className={`strata-card ${proof ? 'border-green-500/30' : ''}`}>
              <CardHeader>
                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                  <Diamond className="w-5 h-5 text-amber-500" />
                  Proof Document
                  {proof && <Check className="w-5 h-5 text-green-500 ml-auto" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {proof ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-amber-600/10 border border-amber-600/20">
                      <p className="text-white font-medium mb-2">Executive Summary</p>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {proof.executive_summary}
                      </p>
                    </div>

                    {proof.key_strengths?.length > 0 && (
                      <div>
                        <p className="text-slate-400 text-sm mb-2">Key Strengths</p>
                        <div className="flex flex-wrap gap-2">
                          {proof.key_strengths.map((strength, idx) => (
                            <span key={idx} className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm">
                              {strength}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {proof.narratives?.length > 0 && (
                      <div>
                        <p className="text-slate-400 text-sm mb-2">STAR Stories ({proof.narratives.length})</p>
                        <div className="space-y-2">
                          {proof.narratives.slice(0, 2).map((story, idx) => (
                            <div key={idx} className="p-3 rounded bg-slate-mid/30 border border-slate-mid/50">
                              <p className="text-white text-sm font-medium">{story.title}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button 
                      variant="outline" 
                      className="w-full strata-button-secondary"
                      onClick={handleDownloadPDF}
                      disabled={downloading}
                      data-testid="download-proof-pdf"
                    >
                      {downloading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      {downloading ? 'Preparing...' : 'Download PDF'}
                    </Button>
                  </div>
                ) : hasProof && rolefit ? (
                  <div className="space-y-4">
                    <p className="text-slate-400">
                      Generate a polished document showcasing your leadership readiness.
                    </p>
                    <Button 
                      onClick={handleCreateProof}
                      disabled={creating}
                      className="w-full strata-button-primary"
                      data-testid="create-proof-btn"
                    >
                      {creating ? 'Creating...' : 'Generate Proof Document'}
                    </Button>
                  </div>
                ) : hasProof && !rolefit ? (
                  <div className="space-y-4">
                    <p className="text-slate-400">
                      Complete a Role Fit analysis first to generate your Proof document.
                    </p>
                    <Button disabled className="w-full" variant="outline">
                      Requires Role Fit Analysis
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-slate-400">
                      A comprehensive document proving your leadership readiness for specific opportunities.
                    </p>
                    <ul className="space-y-2">
                      {['Executive summary', 'BEI/STAR-format stories', 'Interview strategy', 'Strength showcase'].map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-4 h-4 text-amber-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handlePurchase('proof')}
                      disabled={purchasing || !hasRoleFit}
                      className="w-full strata-button-primary"
                      data-testid="purchase-proof-btn"
                    >
                      {purchasing ? 'Processing...' : hasRoleFit ? 'Unlock for $79' : 'Requires Role Fit ($39)'}
                    </Button>
                    
                    {!hasRoleFit && (
                      <Button 
                        onClick={() => handlePurchase('proof_bundle')}
                        disabled={purchasing}
                        variant="outline"
                        className="w-full strata-button-secondary"
                      >
                        Get Bundle: Strata Record Synthesis + Role Fit + Proof for $129
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bundle Offer */}
        {hasSynthesis && !hasRoleFit && !hasProof && (
          <Card className="strata-card-accent">
            <CardContent className="p-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="font-display text-xl text-white mb-2">Complete Proof Bundle</h3>
                  <p className="text-slate-400">
                    Get Strata Record Synthesis, Role Fit analysis, and Proof document together and save $18.
                  </p>
                </div>
                <Button 
                  onClick={() => handlePurchase('proof_bundle')}
                  disabled={purchasing}
                  className="strata-button-primary whitespace-nowrap"
                >
                  {purchasing ? 'Processing...' : 'Get Bundle for $129'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
